import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { WarrantyCard } from './WarrantyCard';
import { useWarrantyReorder } from '../hooks/useWarrantyReorder';

interface WarrantiesGridViewProps {
  data: any[];
  onRowClick?: (warranty: any) => void;
  onDuplicate?: (uuid: string, name: string) => void;
  onToggleActive?: (uuid: string, isActive: boolean) => void;
  onCardClick?: (uuid: string) => void;
  currentUserType?: 'INTERNAL' | 'EXTERNAL';
  canReorder?: boolean;
  showTransferPrices?: boolean;
  isWarrantyPolicyOwner?: boolean;
  hideMonthlyPrice?: boolean;
  /** Afficher la ligne « Affinicar Assist : Oui / Non » sur chaque vignette. */
  showAssistance?: boolean;
}

export function WarrantiesGridView({ data, onDuplicate, onToggleActive, onCardClick, currentUserType, canReorder = false, showTransferPrices = true, isWarrantyPolicyOwner = true, hideMonthlyPrice = false, showAssistance = false }: WarrantiesGridViewProps) {
  const { reorderByIds } = useWarrantyReorder(data);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    reorderByIds(active.id as string, over.id as string);
  };

  const activeWarranty = activeId ? data.find((w) => w.uuid === activeId) : null;

  // Piste auto-ajustée plutôt que des paliers 1 / 3 / 5 : ceux-ci passaient à cinq
  // colonnes dès 1024 px, soit ~200 px par vignette, où la barre de boutons déborde.
  // `auto-fill` suit la largeur du CONTENEUR — ce qui compte vraiment quand la barre
  // latérale s'ouvre ou se ferme — et garantit un minimum de 240 px par carte.
  const grid = (
    <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(240px,1fr))] tw-gap-4">
      {data.map((warranty) => (
        <WarrantyCard
          key={warranty.uuid}
          warranty={warranty}
          onDuplicate={onDuplicate ? () => onDuplicate(warranty.uuid, warranty.name) : undefined}
          onToggleActive={onToggleActive ? () => onToggleActive(warranty.uuid, warranty.isActive) : undefined}
          onCardClick={onCardClick}
          currentUserType={currentUserType}
          isDragEnabled={canReorder}
          showTransferPrices={showTransferPrices}
          isWarrantyPolicyOwner={isWarrantyPolicyOwner}
          hideMonthlyPrice={hideMonthlyPrice}
          showAssistance={showAssistance}
        />
      ))}
    </div>
  );

  if (!canReorder) return grid;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={data.map((w) => w.uuid)} strategy={rectSortingStrategy}>
        {grid}
      </SortableContext>
      <DragOverlay>
        {activeWarranty ? (
          <div className="tw-opacity-80 tw-shadow-2xl tw-rotate-2 tw-scale-105">
            <WarrantyCard
              warranty={activeWarranty}
              currentUserType={currentUserType}
              isDragEnabled={false}
              showActions={false}
              showTransferPrices={showTransferPrices}
              showAssistance={showAssistance}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
